// Journey.jsx — Our Journey exact mockup matching road page
import React, { useState, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  Rocket, Star, Users, Trophy, Globe, Lightbulb,
  ShieldCheck, TrendingUp, Heart, Award, Zap
} from "lucide-react";

// ── Timeline milestones mapped to the 5 red-marked stops on the clean road template ──
const ERA_MILESTONES = [
  {
    id: 1,
    year: "2020",
    title: "Stop 1 | Team Expansion",
    subtitle: "GROWING OPERATIONAL CAPABILITIES",
    desc: "Expanded our engineer workforce to 15+ professionals, launching dedicated software training programs and scaling our core programming workflows in Pune.",
    icon: <Users size={18} />,
    color: "#243e7c",
    // 960 x 500 coordinates matching user's far-back horizon circle
    pinX: 810,
    pinY: 70,
    scale: 0.65,
    cardLeft: "58%",
    cardTop: "0%",
    cardWidth: "210px",
    leaderPath: "M 760 50 L 810 70"
  },
  {
    id: 2,
    year: "2022",
    title: "Stop 2 | Quality & Scale",
    subtitle: "ISO CERTIFICATION",
    desc: "Achieved the ISO 9001:2015 quality process certification. Expanded our software delivery systems to service 12+ industry sectors including Government.",
    icon: <ShieldCheck size={18} />,
    color: "#248ecb",
    // Coordinates matching user's further-back center-left circle
    pinX: 520,
    pinY: 105,
    scale: 0.75,
    cardLeft: "34%",
    cardTop: "1%",
    cardWidth: "200px",
    leaderPath: "M 500 80 L 520 105"
  },
  {
    id: 3,
    year: "2024",
    title: "Stop 3 | Core Milestone",
    subtitle: "100+ ENTERPRISE PRODUCTS",
    desc: "Successfully crossed the milestone of delivering 100+ bespoke systems. Upgraded our operational standards to fully GST-compliant models.",
    icon: <Trophy size={18} />,
    color: "#16a34a",
    // Coordinates matching user's upper-middle circle
    pinX: 615,
    pinY: 195,
    scale: 0.9,
    cardLeft: "71%",
    cardTop: "46%",
    cardWidth: "220px",
    leaderPath: "M 680 230 L 615 195"
  },
  {
    id: 4,
    year: "2025",
    title: "Stop 4 | AI Integration",
    subtitle: "AI, IOT & CLOUD SERVICES",
    desc: "Established a dedicated division focused on Machine Learning model integration, IoT telemetry, and high-performance cloud databases.",
    icon: <Zap size={18} />,
    color: "#7c3aed",
    // Coordinates matching user's middle circle
    pinX: 420,
    pinY: 245,
    scale: 1.05,
    cardLeft: "18%",
    cardTop: "32%",
    cardWidth: "210px",
    leaderPath: "M 350 220 L 420 245"
  },
  {
    id: 5,
    year: "2026",
    title: "Stop 5 | Future Vision",
    subtitle: "GLOBAL REACH",
    desc: "Expanding international software operations to serve enterprise platforms globally, engineering tomorrow's digital infrastructure with AI-first architecture.",
    icon: <Star size={18} />,
    color: "#f97316",
    // Coordinates matching user's bottom-left circle
    pinX: 250,
    pinY: 375,
    scale: 1.25,
    cardLeft: "3%",
    cardTop: "58%",
    cardWidth: "220px",
    leaderPath: "M 220 350 L 250 375"
  }
];

const STATS = [
  { value: "7+", label: "Years of Excellence", icon: <Award size={24} /> },
  { value: "100+", label: "Projects Delivered", icon: <Trophy size={24} /> },
  { value: "30+", label: "Team Members", icon: <Users size={24} /> },
  { value: "12+", label: "Industries Served", icon: <Globe size={24} /> },
  { value: "98%", label: "Client Satisfaction", icon: <Heart size={24} /> },
  { value: "50+", label: "Happy Clients", icon: <Star size={24} /> }
];

// ── Animations ──────────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } }
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } }
};

// ── Main Component ──────────────────────────────────────────────────────────
const Journey = () => {
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const isStatsInView = useInView(statsRef, { once: true, margin: "-80px" });

  const [activeEra, setActiveEra] = useState(ERA_MILESTONES[0]);
  const [hoveredEra, setHoveredEra] = useState(null);

  return (
    <div style={{
      fontFamily: "'Manrope', sans-serif",
      background: "#ffffff",
      minHeight: "100vh",
      overflowX: "hidden"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,700;1,400;1,700&family=Manrope:wght@300;400;500;600;700;800&display=swap');

        /* Exact 3D Road Background Wrapper */
        .jrny-mockup-road-container {
          position: relative;
          width: 100%;
          max-width: 1100px;
          aspect-ratio: 2816 / 1536;
          background-image: url('/images/Road.png');
          background-size: 100% 100%;
          background-repeat: no-repeat;
          background-position: center;
          margin: 0 auto;
        }

        /* 3D Destination Pins positioned on top of the road */
        .jrny-destination-pin {
          position: absolute;
          transform: translate(-50%, -100%);
          z-index: 10;
          cursor: pointer;
          transition: transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .jrny-3d-pin-core {
          width: 46px;
          height: 46px;
          background: #ffffff;
          border: 3px solid #243e7c;
          box-shadow: 0 12px 28px rgba(36,62,124,0.3), inset 0 2px 5px rgba(0,0,0,0.1);
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: border-color 0.25s, transform 0.25s, box-shadow 0.25s;
        }

        .jrny-destination-pin:hover .jrny-3d-pin-core,
        .jrny-destination-pin.active .jrny-3d-pin-core {
          border-color: #f97316;
          box-shadow: 0 18px 36px rgba(249,115,22,0.45);
          transform: rotate(-45deg) scale(1.15) translateY(-4px);
        }

        .jrny-pin-icon {
          transform: rotate(45deg);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Badge absolute positioned below the pin tip to avoid pushing the pin up */
        .jrny-pin-badge {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
          background: #fff;
          color: #0f172a;
          font-size: 0.65rem;
          font-weight: 800;
          padding: 2px 7px;
          border-radius: 20px;
          margin-top: 4px;
          text-align: center;
          box-shadow: 0 4px 10px rgba(0,0,0,0.08);
          border: 1px solid rgba(36,62,124,0.08);
          letter-spacing: 0.5px;
        }

        /* Always-visible infographics milestone details card overlay on road */
        .jrny-road-infocard {
          position: absolute;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border-radius: 14px;
          padding: 0.9rem;
          box-shadow: 0 10px 25px rgba(36,62,124,0.08);
          z-index: 5;
          transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;
        }

        .jrny-road-infocard.highlighted {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 15px 35px rgba(249,115,22,0.22);
          border-color: #f97316 !important;
        }

        .jrny-stat-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .jrny-stat-card:hover {
          transform: translateY(-6px) scale(1.02);
          box-shadow: 0 20px 40px rgba(36,62,124,0.12) !important;
        }
      `}</style>

      {/* ── Hero Section ── */}
      <div ref={heroRef} style={{
        background: "linear-gradient(135deg, #0a1c3b 0%, #0f2d54 50%, #0a1c3b 100%)",
        padding: "6rem 0 5rem",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <div style={{ position: "absolute", top: "-80px", right: "-80px", width: "400px", height: "400px", borderRadius: "50%", background: "radial-gradient(circle, rgba(36,142,203,0.15) 0%, transparent 70%)", filter: "blur(60px)" }} />
          <div style={{ position: "absolute", bottom: "-60px", left: "-60px", width: "320px", height: "320px", borderRadius: "50%", background: "radial-gradient(circle, rgba(36,62,124,0.18) 0%, transparent 70%)", filter: "blur(60px)" }} />
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "30px 30px" }} />
        </div>

        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <motion.div
            variants={stagger} initial="hidden"
            animate={isHeroInView ? "visible" : "hidden"}
            style={{ textAlign: "center" }}
          >
            <motion.div variants={fadeUp} style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              background: "rgba(36,142,203,0.15)",
              border: "1px solid rgba(36,142,203,0.3)",
              borderRadius: "30px",
              padding: "6px 20px",
              marginBottom: "1.5rem"
            }}>
              <Lightbulb size={14} color="#248ecb" />
              <span style={{ color: "#7dd3fc", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "3px", textTransform: "uppercase" }}>
                Our Story
              </span>
            </motion.div>

            <motion.h1 variants={fadeUp} style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(2.5rem, 6vw, 4.2rem)",
              fontWeight: 700, color: "#f8fafc",
              lineHeight: 1.12, letterSpacing: "-2px",
              marginBottom: "1.25rem"
            }}>
              Our{" "}
              <span style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                background: "linear-gradient(135deg, #248ecb 0%, #39aecb 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}>
                Journey
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "1.1rem", color: "rgba(255,255,255,0.55)",
              lineHeight: 1.8, maxWidth: "580px", margin: "0 auto 2rem"
            }}>
              From a small startup in Pune to a trusted technology partner across 12+ industries — here's how we built The Innovative Solutions, one milestone at a time.
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div ref={statsRef} style={{ padding: "3.5rem 0", background: "rgba(255,255,255,0.5)", backdropFilter: "blur(12px)" }}>
        <div className="container">
          <motion.div
            variants={stagger} initial="hidden"
            animate={isStatsInView ? "visible" : "hidden"}
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "1.25rem" }}
          >
            {STATS.map((s, i) => (
              <motion.div
                key={i} variants={fadeUp}
                className="jrny-stat-card"
                style={{
                  background: "rgba(255,255,255,0.8)",
                  border: "1px solid rgba(36,62,124,0.1)",
                  borderRadius: "18px", padding: "1.5rem 1rem",
                  textAlign: "center",
                  boxShadow: "0 4px 16px rgba(36,62,124,0.06)"
                }}
              >
                <div style={{ color: "#243e7c", marginBottom: "0.5rem", display: "flex", justifyContent: "center" }}>{s.icon}</div>
                <div style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "2rem", fontWeight: 700,
                  color: "#243e7c", letterSpacing: "-1px", lineHeight: 1,
                  marginBottom: "0.3rem"
                }}>
                  {s.value}
                </div>
                <div style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: "0.75rem", fontWeight: 600,
                  color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px"
                }}>
                  {s.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── Road Timeline Section ── */}
      <div style={{ padding: "6rem 0 8rem" }}>
        <div className="container">

          {/* Section header */}
          <div style={{ textAlign: "center", marginBottom: "5rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", marginBottom: "1rem" }}>
              <span style={{ display: "block", width: "32px", height: "1.5px", background: "linear-gradient(90deg, transparent, rgba(36,62,124,0.4), transparent)", borderRadius: "2px" }} />
              <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "3.5px", color: "rgba(36,62,124,0.55)", textTransform: "uppercase" }}>
                MILESTONES
              </span>
              <span style={{ display: "block", width: "32px", height: "1.5px", background: "linear-gradient(90deg, transparent, rgba(36,62,124,0.4), transparent)", borderRadius: "2px" }} />
            </div>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: 700, color: "#0f172a",
              letterSpacing: "-1px", margin: "0 0 1rem"
            }}>
              Winding Road to{" "}
              <span style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                background: "linear-gradient(135deg, #243e7c, #248ecb)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                backgroundClip: "text"
              }}>
                Success
              </span>
            </h2>
            <p style={{ fontFamily: "'Manrope', sans-serif", fontSize: "1rem", color: "#64748b", maxWidth: "520px", margin: "0 auto" }}>
              Explore our journey milestone improvements along the winding road below.
            </p>
          </div>

          {/* ────────────────── DESKTOP VIEW: exact mockup background ────────────────── */}
          <div className="d-none d-lg-block position-relative">
            <div className="jrny-mockup-road-container">

              {/* Leader pointer lines layer connecting description cards to road pins */}
              <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 3, pointerEvents: "none" }} viewBox="0 0 960 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                {ERA_MILESTONES.map((item, i) => {
                  const isHighlighted = hoveredEra && hoveredEra.id === item.id;
                  return (
                    <AnimatePresence key={`line-anim-${i}`}>
                      {isHighlighted && (
                        <motion.path
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.25, ease: "easeOut" }}
                          d={item.leaderPath}
                          stroke="#f97316"
                          strokeWidth="2"
                          strokeDasharray="4, 4"
                          fill="none"
                        />
                      )}
                    </AnimatePresence>
                  );
                })}
              </svg>

              {/* Pulsing Hotspots / Destination Pins overlay */}
              {ERA_MILESTONES.map((item) => {
                const isHighlighted = hoveredEra && hoveredEra.id === item.id;
                const isActive = activeEra.id === item.id || isHighlighted;
                return (
                  <div
                    key={`pin-${item.id}`}
                    className={`jrny-destination-pin ${isActive ? "active" : ""}`}
                    style={{
                      left: `${(item.pinX / 960) * 100}%`,
                      top: `${(item.pinY / 500) * 100}%`
                    }}
                    onMouseEnter={() => setHoveredEra(item)}
                    onMouseLeave={() => setHoveredEra(null)}
                    onClick={() => setActiveEra(item)}
                  >
                    <div
                      className="jrny-3d-pin-core"
                      style={{
                        transform: `rotate(-45deg) scale(${item.scale})`,
                        borderColor: isActive ? "#f97316" : item.color
                      }}
                    >
                      <div className="jrny-pin-icon" style={{ color: isActive ? "#f97316" : "#243e7c" }}>
                        {item.icon}
                      </div>
                    </div>
                    <div className="jrny-pin-badge" style={{ transform: `scale(${item.scale})`, borderColor: isActive ? "#f97316" : `${item.color}35` }}>
                      {item.year}
                    </div>
                  </div>
                );
              })}

              {/* Milestone details cards placed directly on the road graphic layout - only visible on hover */}
              {ERA_MILESTONES.map((item) => {
                const isHighlighted = hoveredEra && hoveredEra.id === item.id;
                return (
                  <AnimatePresence key={`card-${item.id}`}>
                    {isHighlighted && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.94, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.94, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="jrny-road-infocard highlighted"
                        style={{
                          left: item.cardLeft,
                          top: item.cardTop,
                          width: item.cardWidth,
                          border: `2px solid #f97316`,
                          borderLeft: `4px solid #f97316`
                        }}
                        onMouseEnter={() => setHoveredEra(item)}
                        onMouseLeave={() => setHoveredEra(null)}
                      >
                        <span style={{
                          display: "inline-block",
                          background: `${item.color}10`,
                          color: item.color,
                          fontSize: "0.6rem",
                          fontWeight: 800,
                          padding: "1px 7px",
                          borderRadius: "10px",
                          marginBottom: "0.25rem",
                          letterSpacing: "0.5px"
                        }}>
                          {item.year} Improvement
                        </span>
                        <h4 style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: "0.85rem",
                          fontWeight: 750,
                          color: "#0f172a",
                          margin: "0 0 3px"
                        }}>
                          {item.title.split("|")[1].trim()}
                        </h4>
                        <p style={{
                          fontFamily: "'Manrope', sans-serif",
                          fontSize: "0.72rem",
                          lineHeight: 1.4,
                          color: "#475569",
                          margin: 0
                        }}>
                          {item.desc}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                );
              })}

            </div>
          </div>

          {/* ────────────────── MOBILE VIEW: Simple clean stack of cards ────────────────── */}
          <div className="d-block d-lg-none" style={{ maxWidth: "600px", margin: "0 auto", padding: "0 1rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {ERA_MILESTONES.map((item) => (
                <div
                  key={`mob-card-${item.id}`}
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    border: `1px solid ${item.color}20`,
                    borderLeft: `5px solid ${item.color}`,
                    borderRadius: "16px",
                    padding: "1.5rem",
                    boxShadow: "0 8px 24px rgba(36, 62, 124, 0.05)"
                  }}
                >
                  <span style={{
                    display: "inline-block",
                    background: `${item.color}12`,
                    color: item.color,
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    padding: "3px 12px",
                    borderRadius: "20px",
                    marginBottom: "0.75rem",
                    letterSpacing: "1px"
                  }}>
                    {item.year}
                  </span>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.2rem", fontWeight: 750, color: "#0f172a", margin: "0 0 0.25rem" }}>
                    {item.title.split("|")[1].trim()}
                  </h3>
                  <span style={{ display: "block", fontSize: "0.68rem", fontWeight: 800, color: item.color, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "0.75rem" }}>
                    {item.subtitle}
                  </span>
                  <p style={{ fontSize: "0.9rem", lineHeight: 1.6, color: "#475569", margin: 0 }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── Vision Section ── */}
      <div style={{
        background: "linear-gradient(135deg, #0a1c3b 0%, #0f2d54 60%, #0a1c3b 100%)",
        padding: "5rem 0",
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "28px 28px", pointerEvents: "none" }} />
        <div className="container" style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-80px" }}
          >
            <div style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: "70px", height: "70px", borderRadius: "50%",
              background: "radial-gradient(circle at 35% 35%, #248ecb 0%, #0a1c3b 100%)",
              boxShadow: "0 0 0 8px rgba(36,142,203,0.15), 0 12px 32px rgba(36,142,203,0.3)",
              marginBottom: "2rem",
              color: "#fff"
            }}>
              <TrendingUp size={28} />
            </div>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
              fontWeight: 700, color: "#f8fafc",
              letterSpacing: "-1px", marginBottom: "1.25rem"
            }}>
              The Road Ahead
            </h2>
            <p style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "1.15rem", fontStyle: "italic",
              color: "rgba(255,255,255,0.6)", lineHeight: 1.8,
              maxWidth: "600px", margin: "0 auto 2.5rem"
            }}>
              "We are not just building software — we are building the digital infrastructure of tomorrow. Our journey continues with global ambitions, AI-first thinking, and an unshakeable commitment to our clients."
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "1rem", flexWrap: "wrap" }}>
              <a href="/contact" style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                background: "linear-gradient(135deg, #248ecb, #243e7c)",
                color: "#fff", fontWeight: 800, fontSize: "0.85rem",
                letterSpacing: "0.5px", padding: "12px 28px",
                borderRadius: "50px", textDecoration: "none",
                boxShadow: "0 8px 24px rgba(36,142,203,0.35)",
                textTransform: "uppercase"
              }}>
                <Rocket size={16} /> Start Your Journey With Us
              </a>
              <a href="/about" style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "rgba(255,255,255,0.8)", fontWeight: 700, fontSize: "0.85rem",
                letterSpacing: "0.5px", padding: "12px 28px",
                borderRadius: "50px", textDecoration: "none",
                textTransform: "uppercase"
              }}>
                About Us
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Journey;
